import {xhrHelper} from './../../../utils/xhrHelper';
import {
  updateDetailsStaticFetchStatus,
  updateDetailsStaticData,
  updateDetailsHermesFetchStatus,
  updateDetailsHermesData,
  updateDetailsPageConfigAction,

  // hotel policy
  updateHotelPolicyList,
  updateHotelPolicyAdditionalData,
  updateHotelPolicyFetchStatus,
  updateHotelPolicyError,

  // similar hotels
  updateSimilarHotels,
  updateSimilarHotelsFetchStatus,
} from './actions';
import {AppState} from './../../../store';
import {ThunkAction, ThunkDispatch} from 'redux-thunk';
import {AnyAction} from 'redux';
import {prepareDetailsPageHermesCall} from '../utils/prepareDetailsPageHermesCall';
import {prepareDetailsPageStaticDataCall} from '../utils/prepareDetailsPageStaticDataCall';
import {parseDetailsPageUrlToConfig} from '../utils/parseDetailsPageUrlToConfig';
import {updateQueryAction} from '../../common/redux/actions';
import {IUpdate_Query} from '../../common/redux/types';
import {IUpdate_Details_Config_Action, FetchStatusType, DetailsHermesShape} from './types';
import fetchHotelPolicy from '../utils/fetch-hotel-policy';
import {sendEvarData} from '@ibibo/analytics';

import {hotelIdSelector, hotelPolicyThunkSelector, similarHotelsThunkSelector} from '../selectors';

import {parseDateIntoHermesString} from '../../../utils/date-utils';
import fetchSimilarHotels from '../utils/fetch-similar-hotels';
import {getHotelDetailsData} from '@ibibo/get-hotel-detail-page-data/lib/get-hotel-detail-page-hermes-data';

export const fetchDetailsStaticDataThunk = (): ThunkAction<void, AppState, {}, AnyAction> => {
  // Invoke API
  return (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    // getState()
    // 🐨 change Array<any> to Array<{typeof staticData}>
    dispatch(updateDetailsStaticFetchStatus({isLoading: true}));

    const {details, common} = getState();
    
    const {destination} = common;
    const {vhid, filter} = details;

    const url = prepareDetailsPageStaticDataCall(vhid || destination._id, filter);

    return xhrHelper<any, Array<any>>({
      name: 'details static',
      type: 'GET',
      url,
    })
      .then(response => {
        dispatch(updateDetailsStaticData(response.data[0]));
        dispatch(updateDetailsStaticFetchStatus({isLoading: false}));
      })
      .catch(err => {
        console.trace(err);
      });
  };
};

export const fetchDetailsHermesDataThunk = ():
ThunkAction<void, AppState, {}, AnyAction> => {
  // Invoke API
  return (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    // getState()
    // 🐨 change Array<any> to Array<{typeof staticData}>

    dispatch(updateDetailsHermesFetchStatus({isLoading: true}));

    const reduxState = getState();

    const {common, details } = reduxState;
    const {destination, checkin, checkout, roomString, country} = common;
    const {selectedHotelStatic, filter, staticData, offerCodeFromQueryParam } = details;

    const vhid = hotelIdSelector(reduxState);

    const url = prepareDetailsPageHermesCall({
      vhid,
      cityId: staticData.vcid,
      checkin,
      checkout,
      roomString,
      staticData,
      selectedHotelStatic,
      filter,
      offerCodeFromQueryParam,
    });


    

    return getHotelDetailsData({url})
      .then(response => {
        dispatch(updateDetailsHermesData(response as DetailsHermesShape));
        dispatch(updateDetailsHermesFetchStatus({isLoading: false}));
      })
      .catch(err => {
        console.trace(err);
        sendEvarData({
          eVar22: err.message,
        })
      });
  };
};

export const fetchHotelPolicyDataThunk = (): ThunkAction<void, AppState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const reduxState = getState();

    let {
      checkinObj,
      checkoutObj,
      roomstring,
      paxSplittedRoomString,
      cityId,
      hotelCode,
      roomsDataHermes,
      oldPax,
      hotelPolicyFromHermesData,
    } = hotelPolicyThunkSelector(reduxState);

    const {showHotelPolicyFromHermes, hotelPolicyListFromHermes} = hotelPolicyFromHermesData;

    const showPolicyFromHermes =
      showHotelPolicyFromHermes &&
      hotelPolicyListFromHermes &&
      Array.isArray(hotelPolicyListFromHermes) &&
      hotelPolicyListFromHermes.length;

    const checkin = parseDateIntoHermesString(checkinObj.date);
    const checkout = parseDateIntoHermesString(checkoutObj.date);

    if (paxSplittedRoomString) {
      roomstring = paxSplittedRoomString;
    }

    if (oldPax) {
      roomstring = oldPax;
    }

    const firstRoom = roomsDataHermes[0] || ({} as typeof roomsDataHermes[0]);

    const {rpl: flavorsOfFirstRoom = []} = firstRoom;
    const firstFavorOfFirstRoom = flavorsOfFirstRoom[0] || ({} as typeof flavorsOfFirstRoom[0]);
    const {fwdp = {}, ibp} = firstFavorOfFirstRoom;

    dispatch(updateHotelPolicyFetchStatus(FetchStatusType.PENDING));

    const [hotelPolicyData, hotelPolicyError] = await fetchHotelPolicy({
      checkin,
      checkout,
      pax: roomstring,
      cityId,
      hotelCode,
      forwardParams: fwdp,
      vendor: ibp,
    });

    if (hotelPolicyData.hotelPolicyList) {
      dispatch(updateHotelPolicyFetchStatus(FetchStatusType.RESOLVED));

      /**
       * if hotel policy has *NOT* to be shown from hermes, update hotel policy list
       * with the freshly fetched list
       */

      if (!showPolicyFromHermes) {
        dispatch(updateHotelPolicyList(hotelPolicyData.hotelPolicyList));
      }
    }

    /**
     * if hotel policy has to be shown from hermes
     */
    if (showPolicyFromHermes) {
      dispatch(updateHotelPolicyFetchStatus(FetchStatusType.RESOLVED));
      dispatch(updateHotelPolicyList(hotelPolicyListFromHermes));
    }

    if (hotelPolicyData.additionalData) {
      dispatch(updateHotelPolicyAdditionalData(hotelPolicyData.additionalData));
    }

    if (hotelPolicyError) {
      dispatch(updateHotelPolicyError(hotelPolicyError));
    }
  };
};

export const fetchSimilarHotelsThunk = (): ThunkAction<void, AppState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const reduxState = getState();

    let {
      checkinObj,
      checkoutObj,
      roomstring,
      paxSplittedRoomString,
      cityId,
      hotelCode,
    } = similarHotelsThunkSelector(reduxState);

    const checkin = parseDateIntoHermesString(checkinObj.date);
    const checkout = parseDateIntoHermesString(checkoutObj.date);

    if (paxSplittedRoomString) {
      roomstring = paxSplittedRoomString;
    }

    dispatch(updateSimilarHotelsFetchStatus(FetchStatusType.PENDING));

    const [similarHotelsData, error] = await fetchSimilarHotels({
      checkinYYYYMMDD: checkin,
      checkoutYYYYMMDD: checkout,
      pax: roomstring,
      cityId,
      hotelCode,
    });

    if (similarHotelsData) {
      dispatch(
        updateSimilarHotels({
          list: similarHotelsData.data || [],
          cityMetaInfo: similarHotelsData.city_meta_info,
        }),
      );
    } else {
      dispatch(updateSimilarHotelsFetchStatus(FetchStatusType.RESOLVED));
    }

    if (error) {
      dispatch(updateSimilarHotelsFetchStatus(FetchStatusType.REJECTED));
    }
  };
};
